import React, {
    CSSProperties,
    SyntheticEvent,
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useFocusRing, usePress } from 'react-aria';
import { genericMemo, mergeRefs } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';

import { cn } from '../../lib/css/className/className';
import { TestProps } from '../../internal/types/testTypes';
import type { DataAttributesProps } from '../../internal/types/dataAttributesTypes';
import { getDataAttributesProps } from '../../lib/react/getDataAttributesProps/getDataAttributesProps';

import './Image.css';

const cls = cn('Image');

export interface ImageProps extends TestProps, DataAttributesProps {
    /**
     * Альтернативный текст
     */
    alt: string;

    /**
     * Ширина изображения
     */
    width: number | string;

    /**
     * Высота изображения
     */
    height: number | string;

    /**
     * Ссылка на изображение
     */
    src?: string;

    /**
     * Скругление изображения в пикселях.
     * Размеры определены дизайн-системой
     */
    borderRadius?: '4' | '8' | '10' | '12' | '14' | 'round';

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Ссылка на изображение при неудачной загрузке
     */
    fallbackSrc?: string;

    /**
     * Обработчик нажатия на элемент
     */
    onClick?: () => void;

    /**
     * Обработчик неудачной загрузки
     */
    onError?: (e: SyntheticEvent<HTMLImageElement>) => void;

    /**
     * Обработчик завершения загрузки
     * @deprecated
     */
    onLoaded?: () => void;

    /**
     * Текст во всплывающей подсказке
     */
    title?: string;

    /**
     * Подпись к изображению для screen-readers
     * @default alt
     */
    'aria-label'?: string;

    /**
     * Набор источников для создания адаптивных изображений.
     * [Подробнее об атрибуте](https://developer.mozilla.org/ru/docs/Web/HTML/Element/img#attr-srcset)
     */
    srcSet?: string;

    /**
     * Набор размеров для создания адаптивных изображений.
     * [Подробнее об атрибуте](https://developer.mozilla.org/ru/docs/Web/HTML/Element/img#attr-sizes)
     */
    sizes?: string;

    /**
     * Атрибут способа загрузки изображения.
     * [Подробнее об атрибуте](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading)
     */
    loading?: 'lazy' | 'eager';

    /**
     * Отключает скрытие изображения до события load и анимацию появления.
     * Подходит для изображений, которые должны быть видны без выполнения клиентского JavaScript.
     *
     * @default false
     */
    disableLoadTransition?: boolean;

    /**
     * Параметр участия в последовательной навигации (для кликабельных изображений)
     */
    tabIndex?: number;

    /**
     * Атрибут способа размещения изображения в границах размера
     * [Подробнее об атрибуте](https://developer.mozilla.org/ru/docs/Web/CSS/object-fit)
     */
    fit?: 'contain' | 'cover' | 'fill';

    /**
     * Атрибут выравнивания изображения по горизонтали и вертикали
     * [Подробнее об атрибуте](https://developer.mozilla.org/ru/docs/Web/CSS/object-position)
     */
    position?: {
        x: number;
        y: number;
    };

    /**
     * Фон картинки, который виден, если картинка вписывается в размеры (fitInSize = true)
     * @defaultValue none
     */
    background?: 'gray' | 'white' | 'none';

    /**
     * Параметры для логирования изображения
     * временное решение пока идет эксперимент https://st.yandex-team.ru/DIRECT-229136
     */
    logData?: ImageLogDataProps;
}

export interface ImageLogDataProps {
    namespace: string;
    imageContext: string;
}

// TODO DIRECT-222878 Добавить Skeleton?
const emptyImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';

const ANIMATION_DELAY_MS = 50;

/**
 * Компонент для загрузки изображений.
 * Если изображение не удалось загрузить, то покажется стандартная картинка из свойства `fallback`.
 */
export const Image = genericMemo(
    forwardRef<HTMLImageElement, ImageProps>(function Image(
        {
            alt,
            width,
            height,
            src,
            borderRadius,
            className,
            fallbackSrc,
            onClick,
            onError,
            onLoaded,
            title,
            srcSet,
            sizes,
            loading,
            disableLoadTransition = false,
            tabIndex,
            fit,
            background = 'none',
            position,
            logData,
            ...otherProps
        },
        forwardedRef,
    ) {
        const imageRef = useRef<HTMLImageElement>(null);
        const loadingStartTimeRef = useRef(0);
        const imageSrc = src || emptyImage;
        const { logTech } = useLogger('Image');

        // проверка загрузки изображения
        const [isLoaded, setLoaded] = useState(false);

        // Нужно, потому что событие load не срабатывает, если изображение уже загружено,
        // поэтому смотрим на imageRef.current.complete
        useEffect(() => {
            if (!isLoaded && imageRef.current && imageRef.current.complete) {
                setLoaded(true);
                onLoaded?.();
            }
        }, [isLoaded, onLoaded]);

        useEffect(() => {
            loadingStartTimeRef.current = performance.now();
        }, []);

        const handleLoad = useCallback(() => {
            // временное решение пока идет эксперимент https://st.yandex-team.ru/DIRECT-229136
            if (imageRef.current && logData) {
                const { naturalWidth, naturalHeight } = imageRef.current;
                logTech('loadedImageData', {
                    url: src,
                    width: naturalWidth,
                    height: naturalHeight,
                    loadingTime: performance.now() - loadingStartTimeRef.current,
                    ...logData,
                });
            }

            setLoaded(true);
            onLoaded?.();
        }, [logTech, src, logData, imageRef, onLoaded]);

        const isRendered = useCallback(() => {
            if (!imageRef.current) {
                return false;
            }

            const { naturalWidth, naturalHeight } = imageRef.current;

            return naturalWidth > 0 && naturalHeight > 0;
        }, [imageRef]);

        // анимирование плавного появления изображения
        const [needAnimation, setNeedAnimation] = useState(false);

        useEffect(() => {
            if (disableLoadTransition) {
                return undefined;
            }

            const timer = setTimeout(() => {
                if (!isLoaded && !isRendered()) {
                    setNeedAnimation(true);
                }
            }, ANIMATION_DELAY_MS);

            return () => clearTimeout(timer);
        }, [disableLoadTransition, isLoaded, isRendered, setNeedAnimation]);

        // проверка на возникновение ошибок загрузки
        const [isFailed, setFailed] = useState(false);
        const handleError = useCallback(
            (e: SyntheticEvent<HTMLImageElement>) => {
                onError?.(e);
                setFailed(true);
            },
            [onError],
        );
        useEffect(() => {
            setFailed(false);
        }, [imageSrc]);

        // установка дополнительных стилей
        const style = useMemo<CSSProperties>(() => {
            const radius = borderRadius === 'round' ? '9999' : borderRadius;

            return {
                ...(radius
                    ? {
                          borderRadius: `${radius}px`,
                      }
                    : {}),
                ...(position
                    ? {
                          objectPosition: `${position.x}px ${position.y}px`,
                      }
                    : {}),
            };
        }, [borderRadius, position]);

        // фокус и обработка нажатия для кликабельных изображений
        const { focusProps, isFocusVisible } = useFocusRing();
        const { pressProps } = usePress({
            onPress: () => onClick?.(),
        });

        // TODO DIRECT-222878 Вместо ручного tabIndex можно оборачивать кликабельную картинку в button,
        //  тогда tabIndex будет автоматический

        return (
            <img
                className={cls(
                    {
                        loaded: isLoaded || isFailed,
                        disableLoadTransition,
                        loading: !disableLoadTransition && needAnimation && !isFailed,
                        animated: !disableLoadTransition && needAnimation && isLoaded,
                        fit,
                        background: isFailed ? 'gray' : background,
                        focused: isFocusVisible,
                        clickable: Boolean(onClick),
                    },
                    [className],
                )}
                ref={mergeRefs(imageRef, forwardedRef)}
                src={isFailed && fallbackSrc ? fallbackSrc : imageSrc}
                srcSet={isFailed && fallbackSrc ? fallbackSrc : srcSet}
                sizes={sizes}
                width={width}
                height={height}
                style={style}
                alt={alt}
                {...getDataAttributesProps(otherProps)}
                aria-label={otherProps['aria-label'] || alt}
                data-testid={otherProps['data-testid']}
                title={title}
                onError={handleError}
                onLoad={handleLoad}
                loading={loading}
                tabIndex={onClick && tabIndex}
                {...(onClick && focusProps)}
                {...(onClick && pressProps)}
            />
        );
    }),
);
